import { useAnalytics } from '@sixfold/app-analytics-framework';
import { useErrorHandler } from '@sixfold/app-utils';
import { Button, Input, Stack } from '@sixfold/common-ui';
import { Localized, useLanguage, useLocalization } from '@sixfold/localization-component';
import { NestedError } from '@sixfold/typed-primitives';
import { request } from 'graphql-request';
import * as React from 'react';
import { useNavigate } from 'react-router';

import { BackendError } from '../../../../shared/types/error';
import { buildUrl } from '../../../lib/build_url';
import { isValidEmail } from '../../../lib/form';
import * as Routes from '../../../lib/routes';

type SharedViewLoginFormError = 'emailMissing' | 'emailInvalid';

const sendSharedViewAccessLoginEmailMutation = `
  mutation SendSharedViewAccessLoginEmail($email: String!, $locale: String) {
    sendSharedViewAccessLoginEmail(email: $email, locale: $locale)
  }
`;

export const SharedViewLoginTab: React.FC<{ onEmailSent: (email: string) => void }> = ({ onEmailSent }) => {
  const analytics = useAnalytics();
  const navigate = useNavigate();
  const handleError = useErrorHandler();
  const language = useLanguage();
  const [email, setEmail] = React.useState('');
  const [error, setError] = React.useState<SharedViewLoginFormError | undefined>(undefined);
  const [isLoading, setIsLoading] = React.useState(false);

  const emailPlaceholder = useLocalization('sharedView.login.email.input.placeholder', 'john.smith@example.com');

  const handleSubmit = React.useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (email.length === 0) {
        setError('emailMissing');
        return;
      }

      if (!isValidEmail(email)) {
        setError('emailInvalid');
        return;
      }

      setError(undefined);
      setIsLoading(true);
      analytics?.track('Sent request to login via shared view email');

      try {
        await request('/public/graphql', sendSharedViewAccessLoginEmailMutation, { email, locale: language });
        onEmailSent(email);
      } catch (caughtError) {
        handleError?.captureError(
          new NestedError('Shared view login via email failed', caughtError, { metadata: { email } }),
          { tags: { source: 'sharedViewEmailLogin' } },
        );

        void navigate(
          buildUrl(Routes.Redirect.generatePath({}), [
            { key: 'error_type', value: JSON.stringify({ type: BackendError.SHARED_VIEW }) },
          ]),
        );
      } finally {
        setIsLoading(false);
      }
    },
    [analytics, email, handleError, language, navigate, onEmailSent],
  );

  const emailErrorMessage =
    error === 'emailMissing' ? (
      <Localized id="sharedView.login.email.input.missing">Please enter an email address.</Localized>
    ) : error === 'emailInvalid' ? (
      <Localized id="sharedView.login.email.input.invalid">Please enter a valid email address.</Localized>
    ) : undefined;

  return (
    <Stack as="form" gap="medium" onSubmit={handleSubmit}>
      <Input
        label={<Localized id="sharedView.login.email.input.label">Email address</Localized>}
        placeholder={emailPlaceholder}
        value={email}
        name="email"
        autoComplete="email"
        required
        onChange={({ value }) => {
          setEmail(value);
          setError(undefined);
        }}
        errorMessage={emailErrorMessage}
        invalid={emailErrorMessage !== undefined}
      />
      <Button fullWidth size="medium" kind="primary" type="submit" loading={isLoading}>
        <Localized id="sharedView.login.button.email">Send login link</Localized>
      </Button>
    </Stack>
  );
};
